import React, { useState, useEffect } from "react";
import { MapPin, Users, Calendar, FileText, AlertTriangle } from "lucide-react";
import Modal from "../../common/Modal";
import Button from "../../common/Button";
import FormControl from "../../common/FormControl";
import sopInstanceService, {
  type InitiationData,
  type LocationLite,
  TRANSACTION_TYPES,
} from "../../../services/sopInstanceService";
import type { ToastMessage } from "../../common/Toast";
import { useFieldValidation } from "../../../validation/useFieldValidation";
import { ValidatedInput } from "../../../validation/ValidatedFields";

interface Props {
  processId: number;
  processName: string;
  onClose: () => void;
  onSuccess: () => void;
  setToast: (t: ToastMessage) => void;
}

const InitiateSopModal: React.FC<Props> = ({ processId, processName, onClose, onSuccess, setToast }) => {
  const [initData, setInitData] = useState<InitiationData | null>(null);
  const [allLocations, setAllLocations] = useState<LocationLite[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [geographyOverride, setGeographyOverride] = useState(false);

  const [locationId, setLocationId] = useState<number | null>(null);
  const [populationId, setPopulationId] = useState<number | null>(null);
  const [transactionType, setTransactionType] = useState<string>("Monthly");
  const [sampleText, setSampleText] = useState("");
  const vh = useFieldValidation();

  // Reset validation errors whenever this (always-open) modal mounts.
  useEffect(() => { vh.reset(); }, []);

  useEffect(() => {
    (async () => {
      try {
        const [data, locs] = await Promise.all([
          sopInstanceService.getInitiationData(processId),
          sopInstanceService.listEntityLocations(),
        ]);
        setInitData(data);
        setAllLocations(locs);
        setLocationId(data.default_location_id ?? null);
      } catch {
        setToast({ type: "error", message: "Failed to load initiation data." });
      } finally {
        setLoading(false);
      }
    })();
  }, [processId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!vh.validateAll([
      { name: "sample_text", code: 110, value: sampleText },
    ])) return;
    setSaving(true);
    try {
      await sopInstanceService.createInstance({
        process_id: processId,
        location_id: locationId,
        geography_override: geographyOverride,
        population_id: populationId,
        transaction_type: transactionType,
        sample_text: sampleText.trim() || null,
      });
      setToast({ type: "success", message: `SOP "${processName}" initiated successfully.` });
      onSuccess();
    } catch (err: any) {
      setToast({ type: "error", message: err?.response?.data?.detail || "Failed to initiate SOP." });
    } finally {
      setSaving(false);
    }
  };

  const locationOptions = geographyOverride
    ? allLocations.map((l) => ({ value: String(l.id), label: l.name }))
    : (initData?.my_location_ids ?? [])
        .map((id) => allLocations.find((l) => l.id === id))
        .filter(Boolean)
        .map((l) => ({ value: String(l!.id), label: l!.name }));

  return (
    <Modal isOpen onClose={onClose} size="md">
      <Modal.Header onClose={onClose}>
         Initiate SOP - {processName}
      </Modal.Header>

      <Modal.Body>
        {loading ? (
          <div className="py-10 text-center text-sm text-gray-400 animate-pulse">Loading…</div>
        ) : (
          <form id="initiate-sop-form" onSubmit={handleSubmit} className="space-y-5">

            {/* Geography */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <MapPin size={14} className="text-blue-500" />
                <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">Geography</span>
              </div>

              {initData?.default_location_name && !geographyOverride && (
                <div className="mb-2 px-3 py-2 bg-blue-50 border border-blue-100 rounded-lg text-xs text-blue-700 flex items-center gap-2">
                  <MapPin size={11} className="flex-shrink-0" />
                  <span>Your posting location: <strong>{initData.default_location_name}</strong></span>
                </div>
              )}

              <FormControl.SearchSelect
                label="Location"
                options={locationOptions}
                value={locationId != null ? String(locationId) : ""}
                onChange={(v) => setLocationId(v ? Number(v) : null)}
                placeholder="Select geography…"
              />

              <div className="mt-2">
                <FormControl.Checkbox
                  label="Use a different geography"
                  checked={geographyOverride}
                  onChange={(checked) => {
                    setGeographyOverride(checked);
                    if (!checked) setLocationId(initData?.default_location_id ?? null);
                  }}
                />
              </div>
            </div>

            {/* Population */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Users size={14} className="text-purple-500" />
                <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">Population</span>
              </div>
              <FormControl.SearchSelect
                label=""
                options={[
                  { value: "", label: "— None —" },
                  ...(initData?.populations ?? []).map((p) => ({ value: String(p.id), label: p.population_name })),
                ]}
                value={populationId != null ? String(populationId) : ""}
                onChange={(v) => setPopulationId(v ? Number(v) : null)}
                placeholder="Select population…"
              />
            </div>

            {/* Transaction type */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Calendar size={14} style={{ color: "var(--interactive-primary-bg)" }} />
                <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">Transaction Type</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {TRANSACTION_TYPES.map((t) => {
                  const active = transactionType === t;
                  return (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setTransactionType(t)}
                      className="px-3 py-1.5 rounded-lg text-xs font-medium border transition"
                      style={
                        active
                          ? {
                              background: "var(--interactive-primary-bg)",
                              color: "#fff",
                              borderColor: "var(--interactive-primary-bg)",
                            }
                          : {
                              background: "#fff",
                              color: "var(--text-secondary)",
                              borderColor: "var(--border-default)",
                            }
                      }
                    >
                      {t}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Sample */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <FileText size={14} className="text-green-500" />
                <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">Sample</span>
                <span className="text-[10px] text-gray-400">(employee code or reference)</span>
              </div>
              <ValidatedInput
                vh={vh}
                name="sample_text"
                code={110}
                placeholder="e.g. EMP-00123, EMP-00456"
                value={sampleText}
                onChange={(e) => setSampleText(e.target.value)}
                maxLength={500}
                data-testid="INP_sample_text"
              />
            </div>

            {/* Step count info */}
            {initData && initData.step_count > 0 && (
              <div className="flex items-start gap-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-100 text-xs text-gray-500">
                <AlertTriangle size={12} className="mt-0.5 text-amber-400 flex-shrink-0" />
                This SOP has <strong>{initData.step_count}</strong> step{initData.step_count !== 1 ? "s" : ""}. Each authority involved will see their tasks in their Action Center.
              </div>
            )}
          </form>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button
          type="submit"
          form="initiate-sop-form"
          variant="fill"
          disabled={loading || saving || !transactionType}
          data-testid="BTN_initiate_sop"
        >
          Initiate SOP
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default InitiateSopModal;
